import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import {
  idleLimitPassed,
  idleLocked,
  inOneTab,
  openSessionChannel,
  readLastActivity,
  readSessionEnded,
  writeLastActivity,
  type SessionChannel,
} from "@/lib/auth/sessionSync";
import { IDLE_WARNING_SECONDS } from "@/lib/env";
import { logger } from "@/lib/logger";

/**
 * Automatic logoff (FR-14, SEC-14 §164.312(a)(2)(iii)).
 *
 * After the configured idle timeout (`IDLE_TIMEOUT_MINUTES`, set per deployment, served by
 * `GET /api/auth/config`, 30 minutes by default, enforced only here in the browser: the server
 * keeps no idle clock, #1112) with no pointer or keyboard activity in ANY tab of this browser the
 * session ends. A warning appears `IDLE_WARNING_SECONDS` before that, because ending a session
 * silently while someone is reading a long run log is indistinguishable from the app breaking.
 *
 * One clock for the whole browser (round 9, B-18): every tab writes its activity to a shared
 * timestamp (`sessionSync.ts`) and reads it before it warns or signs out, so a background tab
 * nobody touched no longer signs out the person typing in another one. "I'm still here" in one
 * tab dismisses the warning in every tab, and the sign-out itself reaches every tab through
 * `AuthContext`, which audits it once.
 *
 * Activity resets the clock, EXCEPT while the warning is showing: once the
 * countdown is visible the user has to say they are still there. Otherwise a
 * mouse nudged by a passing sleeve keeps a walked-away session open, which is
 * exactly what the control exists to prevent.
 *
 * The sign-out itself goes through AuthContext, which posts
 * `POST /api/v2/auth/sign-out { reason: "idle" }` so the event is audited with
 * `metadata.reason = idle`, then ends the session in this browser only (round 9, fix B).
 */
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "pointerdown", "wheel"] as const;

/** Where a sleeping tab hears that it woke: shown again, focused, or restored from bfcache. */
const WAKE_EVENTS: ReadonlyArray<readonly [() => EventTarget, string]> = [
  [() => document, "visibilitychange"],
  [() => window, "focus"],
  [() => window, "pageshow"],
];

/** The Web Lock the tabs take turns on at the idle logoff (review r9 #7). */
const IDLE_LOGOFF_LOCK = "skydata-idle-logoff";

/** Activity is written to the shared clock at most this often; a key-repeat is not news. */
const ACTIVITY_WRITE_INTERVAL_MS = 1000;

/** One tick of the warning's countdown: down by a second, never below zero. */
function countDownOneSecond(prev: number): number {
  return prev <= 1 ? 0 : prev - 1;
}

export interface IdleTimeoutState {
  /** True while the countdown dialog should be shown. */
  isWarning: boolean;
  secondsRemaining: number;
  /** "I'm still here" — restarts the idle clock, in every tab. */
  continueSession: () => void;
  /** End the session now, audited as a user sign-out. */
  signOutNow: () => void;
}

export function useIdleTimeout(enabled: boolean): IdleTimeoutState {
  const { signOut, endSessionHere, idleTimeoutMinutes } = useAuth();
  // `enabled` gates the RETURNED flag rather than being pushed into state by an
  // effect: a signed-out user cannot be idle, and deriving that keeps the
  // effect below to what it is for — starting and stopping timers.
  const [warningActive, setWarningActive] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(IDLE_WARNING_SECONDS);

  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdown = useRef<ReturnType<typeof setInterval> | null>(null);
  // Read by the activity listener, which must not re-subscribe on every tick.
  const isWarningRef = useRef(false);
  // This tab's last activity; the shared clock may be later (another tab).
  // Set when the clock starts (the effect below), never during render.
  const lastHere = useRef(0);
  const lastWritten = useRef(0);
  // When this tab's warning appeared: activity recorded after it by another tab means the person
  // answered there.
  const warningSince = useRef(0);
  const channel = useRef<SessionChannel | null>(null);

  const idleMs = idleTimeoutMinutes * 60_000;
  const warnAfterMs = Math.max(idleMs - IDLE_WARNING_SECONDS * 1000, 1000);

  const clearTimers = useCallback(() => {
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (countdown.current) clearInterval(countdown.current);
    warningTimer.current = null;
    logoutTimer.current = null;
    countdown.current = null;
  }, []);

  /** The browser's last activity: this tab's, or a later one another tab wrote. */
  const lastActivity = useCallback(() => Math.max(lastHere.current, readLastActivity() ?? 0), []);

  const dismissWarning = useCallback(() => {
    isWarningRef.current = false;
    setWarningActive(false);
    setSecondsRemaining(IDLE_WARNING_SECONDS);
  }, []);

  const endSession = useCallback(
    (reason: "idle" | "user") => {
      clearTimers();
      dismissWarning();
      void signOut(reason);
    },
    [clearTimers, dismissWarning, signOut],
  );

  /**
   * The deadline by the wall clock (round 9 review #1): locked by another tab, or this browser's
   * last activity (this tab's or a later one) older than the limit. Asked before anything counts
   * as activity, so a tab that slept past the deadline cannot revive the session by a key press,
   * an "I'm still here" or a late warning.
   */
  const deadlinePassed = useCallback(
    () => idleLocked() || Date.now() - lastActivity() >= idleMs,
    [lastActivity, idleMs],
  );

  /**
   * Ends a session that is past the deadline. Locked means another tab already ended and audited
   * it, so this tab only ends here (review #4); otherwise the tabs take turns and one audits.
   */
  const endIdle = useCallback(() => {
    clearTimers();
    dismissWarning();
    if (idleLocked()) {
      void endSessionHere("idle");
      return;
    }
    void inOneTab(
      IDLE_LOGOFF_LOCK,
      () => {
        if (idleLocked()) return endSessionHere("idle");
        logger.info("idle timeout reached — signing out");
        return signOut("idle");
      },
      () => endSessionHere("idle"),
    );
  }, [clearTimers, dismissWarning, endSessionHere, signOut]);

  // `scheduleTimers` and `onWarningDue` call each other; the ref breaks the cycle.
  const scheduleRef = useRef<(delayMs?: number) => void>(() => {});

  /** The idle limit is near: warn, unless another tab saw the person since. */
  const onWarningDue = useCallback(() => {
    // A timer that fired late (a sleeping tab) past the deadline ends it, never warns.
    if (deadlinePassed()) {
      endIdle();
      return;
    }
    const quietMs = Date.now() - lastActivity();
    if (quietMs < warnAfterMs) {
      scheduleRef.current(warnAfterMs - quietMs);
      return;
    }
    logger.info("idle timeout warning shown", {
      "skydata.seconds_remaining": IDLE_WARNING_SECONDS,
    });
    warningSince.current = Date.now();
    isWarningRef.current = true;
    setWarningActive(true);
    setSecondsRemaining(IDLE_WARNING_SECONDS);

    countdown.current = setInterval(() => {
      setSecondsRemaining(countDownOneSecond);
    }, 1000);

    logoutTimer.current = setTimeout(() => {
      // Answered in another tab whose message did not arrive: keep going.
      if (lastActivity() > warningSince.current) {
        clearTimers();
        dismissWarning();
        scheduleRef.current();
        return;
      }
      // Another tab got here first and audited it: end this tab without a second row. Tabs whose
      // timers fire together take turns (`inOneTab`), so the second reads the first's mark.
      const endHere = () => {
        logger.info("idle timeout reached in another tab; ending this one");
        clearTimers();
        dismissWarning();
        void endSessionHere("idle");
      };
      void inOneTab(
        IDLE_LOGOFF_LOCK,
        () => {
          const ended = readSessionEnded();
          if (ended !== null && ended >= warningSince.current) return endHere();
          logger.info("idle timeout reached — signing out");
          endSession("idle");
        },
        endHere,
      );
    }, IDLE_WARNING_SECONDS * 1000);
  }, [lastActivity, warnAfterMs, clearTimers, dismissWarning, endSession, endSessionHere]);

  /**
   * Arm the idle clock. Timers only — no state — so the effect below can call
   * it without kicking off a render as it mounts. The warning's own state is
   * set from inside the timeout, which is a callback rather than the effect
   * body.
   */
  const scheduleTimers = useCallback(
    (delayMs: number = warnAfterMs) => {
      if (!enabled) return;
      clearTimers();
      isWarningRef.current = false;
      warningTimer.current = setTimeout(onWarningDue, Math.max(delayMs, 0));
    },
    [enabled, clearTimers, onWarningDue, warnAfterMs],
  );
  useEffect(() => {
    scheduleRef.current = scheduleTimers;
  }, [scheduleTimers]);

  /** "I'm still here": dismiss the warning and start the clock over, in every tab. */
  const continueSession = useCallback(() => {
    // Answered after the deadline (a tab that slept through its countdown): too late.
    if (deadlinePassed()) {
      endIdle();
      return;
    }
    const now = Date.now();
    lastHere.current = now;
    writeLastActivity(now);
    channel.current?.post({ type: "continue", at: now });
    dismissWarning();
    scheduleTimers();
  }, [dismissWarning, scheduleTimers, deadlinePassed, endIdle]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }
    // Activity resets the clock, EXCEPT while the warning is up: once the
    // countdown is visible the user has to say they are still there, or a mouse
    // nudged by a passing sleeve keeps a walked-away session open.
    const onActivity = () => {
      if (isWarningRef.current) return;
      // A key press after the deadline does not revive the session (round 9 review #1).
      if (deadlinePassed()) {
        endIdle();
        return;
      }
      const now = Date.now();
      lastHere.current = now;
      if (now - lastWritten.current >= ACTIVITY_WRITE_INTERVAL_MS) {
        lastWritten.current = now;
        writeLastActivity(now);
      }
      scheduleTimers();
    };
    // Another tab's "I'm still here" answers this tab's warning too.
    const open = openSessionChannel((event) => {
      if (event.type !== "continue") return;
      lastHere.current = Math.max(lastHere.current, event.at);
      if (isWarningRef.current) dismissWarning();
      scheduleTimers();
    });
    channel.current = open;
    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, onActivity, { passive: true }),
    );
    // A tab that slept (a closed lid, a throttled background tab, a page restored from the
    // back-forward cache) checks the wall clock the moment it wakes, before its timers or any
    // request run (round 9 review): past the deadline, or locked by another tab, it ends now.
    const onWake = () => {
      if (document.visibilityState === "hidden") return;
      if (idleLocked() || idleLimitPassed(idleMs) || deadlinePassed()) {
        logger.info("idle limit passed while this tab was asleep — ending the session");
        endIdle();
      }
    };
    WAKE_EVENTS.forEach(([target, event]) => target().addEventListener(event, onWake));
    // The idle limit passed while every tab was closed (or across a reload): the session ends
    // now, audited as idle, rather than counting this tab's opening as activity (review r9 #1).
    // A fresh interactive sign-in writes the clock first (`msalTokenProvider.ts`), so it is
    // never mistaken for a stale one.
    if (idleLimitPassed(idleMs)) {
      // From a timer, not the effect body: the sign-out sets state.
      warningTimer.current = setTimeout(() => {
        logger.info("idle limit passed while no tab was open — ending the session");
        endIdle();
      }, 0);
    } else {
      // A session starting, or a tab opening, counts as activity.
      lastHere.current = Date.now();
      scheduleTimers();
    }
    return () => {
      ACTIVITY_EVENTS.forEach((event) => document.removeEventListener(event, onActivity));
      WAKE_EVENTS.forEach(([target, event]) => target().removeEventListener(event, onWake));
      open.close();
      if (channel.current === open) channel.current = null;
      clearTimers();
    };
  }, [enabled, scheduleTimers, clearTimers, dismissWarning, idleMs, deadlinePassed, endIdle]);

  return {
    isWarning: enabled && warningActive,
    secondsRemaining,
    continueSession,
    signOutNow: () => endSession("user"),
  };
}
